import { asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps, Fragment, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { ICONS } from 'Components/utils/icon/icons';
import { ID_MODAL_HEADER__EASY_ARRIVAL_INFO } from 'Components/utils/identifiers/constants';
import { useFeatureFlip } from 'Src/hooks/use-feature-flip/useFeatureFlip';
import { t } from 'Src/i18n';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Modal } from 'Components/frameworks/modal/Modal';
import { Title } from 'Components/frameworks/title/Title';
import { useMeasure } from 'Components/utils/measure/measure';
type Props = {
  className?: string;
  getOverHeight: (arg0: any) => any;
  onEasyArrivalModalClose: (arg0: any) => any;
};
export const EasyArrivalModal = ({ className, getOverHeight, onEasyArrivalModalClose }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [bind, measure] = useMeasure();
  const height = 'height' in measure ? measure.height : 0;
  const { asPath: path } = useRouter();
  const { cartBookings = [] } = useSelector(
    (state) => state.memberSpace.cart.data?.bookingsCart || {},
  );
  const showEasyArrivalModal = useFeatureFlip('booking.showEasyArrivalModal');
  useEffect(() => {
    if (getOverHeight) {
      getOverHeight(height);
    }
  }, [height, getOverHeight]);
  const isFromBE = path.startsWith('/booking');
  const isFromCC = path.startsWith('/account');
  const hasSomethingInCart = cartBookings.some(({ isEmpty }) => !isEmpty);
  const hasMargin = isFromBE || (isFromCC && hasSomethingInCart);

  if (!showEasyArrivalModal) {
    return null;
  }

  return (
    <Fragment>
      <div
        {...bind}
        className={classnames(
          'isolate w-[405px] max-w-[calc(100vw-40px)] rounded-12 border-1 border-solid border-lightGrey bg-white',
          className,
          {
            'mb-[140px]': hasMargin,
          },
        )}
      >
        <button
          className="text-initial flex items-center gap-10 p-10"
          onClick={() => setIsOpen(true)}
          type="button"
        >
          <Icon
            {...asWidgetProps<ComponentProps<typeof Icon>>({
              alt: t('stays.linkLabels.easyArrivalLabel'),
              name: ICONS.USER_DETAILS,
              width: '7rem',
            })}
          />

          <span className="text-14">
            <span className="block font-brand text-16 font-bold text-sienna">
              {t('common.easyArrival.title')}
            </span>
            <span className="block">{t('common.easyArrival.description')}</span>
            <span className="block text-black underline">{t('common.learnAbout')}</span>
          </span>
        </button>

        <button
          className="absolute end-0 top-0 m-10"
          data-testid="close-button"
          onClick={(e) => {
            e.stopPropagation();
            onEasyArrivalModalClose(undefined);
          }}
          type="button"
        >
          <Icon
            {...asWidgetProps<ComponentProps<typeof Icon>>({ name: ICONS.CROSS, width: '0.75rem' })}
          />
        </button>
      </div>

      {isOpen && (
        <Modal
          {...asWidgetProps<ComponentProps<typeof Modal>>({
            ariaLabel: t('common.easyArrival.info.title'),
            ariaLabelledby: ID_MODAL_HEADER__EASY_ARRIVAL_INFO,
            className: 'flex flex-col items-center gap-20 p-20 text-center md:p-40',
            closeLabel: t('common.close'),
            onClose: () => setIsOpen(false),
            size: 'small',
          })}
        >
          <Icon
            {...asWidgetProps<ComponentProps<typeof Icon>>({
              className: 'shrink-0',
              name: ICONS.USER_DETAILS,
              width: '4rem',
            })}
          />
          <Title
            {...asWidgetProps<ComponentProps<typeof Title>>({
              id: ID_MODAL_HEADER__EASY_ARRIVAL_INFO,
              theme: 'title-brand-n1',
            })}
          >
            {t('common.easyArrival.info.title')}
          </Title>
          <p>{t('common.easyArrival.info.text')}</p>
          <p className="font-bold">{t('common.easyArrival.info.ready')}</p>
        </Modal>
      )}
    </Fragment>
  );
};
